import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import type { PieceTicket } from "@/api/types";
import { formatMoney } from "@/lib/format";
import { DialogShell } from "./DialogShell";
import "./PieceVoidDialog.css";

const REASON_MAX = 200; // PieceTicketEndpoints.MaxReasonLength

/**
 * ef-060-7 — Void an OPEN piece ticket with a reason (1-200 characters). The row is kept (it shows
 * under "Show paid and void"); a voided ticket can never be paid. A synchronous in-flight guard keeps a
 * double click to one request. A 409 (the ticket was paid or voided meanwhile) refreshes the list via
 * `onStale` and keeps the message up.
 */
export function PieceVoidDialog({
  ticket,
  onClose,
  onVoided,
  onStale,
}: {
  ticket: PieceTicket;
  onClose: () => void;
  onVoided: () => void;
  onStale: () => void;
}) {
  const idPrefix = useId();
  const errorId = `${idPrefix}-error`;
  const formId = useId();
  const savingRef = useRef(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (savingRef.current) return;
    const trimmed = reason.trim();
    if (trimmed === "") {
      setMessage("Enter a reason for voiding this ticket.");
      return;
    }
    if (trimmed.length > REASON_MAX) {
      setMessage(`The reason must be ${REASON_MAX} characters or fewer.`);
      return;
    }
    setMessage(null);
    savingRef.current = true;
    setSaving(true);
    try {
      await demoApi.voidPieceTicket(ticket.id, trimmed);
      onVoided();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) onStale();
      setMessage(problemMessage(err, "Could not void this ticket."));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="Void piece ticket"
      size="sm"
      onClose={onClose}
      busy={saving}
      restoreFocus
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--danger" disabled={saving}>
            {saving ? "Voiding…" : "Void ticket"}
          </button>
        </>
      }
    >
      <p className="piece-void__summary">
        {ticket.employeeName} &middot; {ticket.pieceTypeName} &middot;{" "}
        <span className="num">{ticket.units}</span> {ticket.unit} &middot;{" "}
        <span className="num">{formatMoney(ticket.amount)}</span>
      </p>
      <p className="piece-void__hint">A voided ticket is kept on file but is never paid.</p>

      <form
        id={formId}
        className="piece-void__form"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
        aria-busy={saving}
        data-state={saving ? "saving" : message ? "error" : "ready"}
      >
        <div className="piece-void__field">
          <label htmlFor={`${idPrefix}-reason`}>Reason</label>
          <input
            id={`${idPrefix}-reason`}
            type="text"
            value={reason}
            maxLength={REASON_MAX}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? errorId : undefined}
            onChange={(e) => {
              setReason(e.target.value);
              setMessage(null);
            }}
          />
        </div>

        {message && (
          <p id={errorId} className="piece-void__error" role="alert">
            {message}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
